import { api } from './api';
import axios, { AxiosError } from 'axios';
import { createApiCacheKey, cacheApiResponse, getCachedApiResponse } from '../utils/cache';

/**
 * Server-persisted GST registry snapshot (sensitive). Do not send from client on create/update.
 * Shape may vary; treat as opaque JSON with common keys for display helpers.
 */
export type GSTRegistryAudit = Record<string, unknown>;

export interface OrganizationData {
  id: string;
  name: string;
  gst_number: string;
  address?: {
    country: string;
    state: string;
    city: string;
    postal_code: string;
  };
  email?: string;
  business_type?: string;
  industry_sector?: string;
  annual_turnover?: number;
  employee_count?: number;
  /** ISO timestamp when server stored registry snapshot during GST verification on create/update */
  gst_verified_at?: string;
  /** Internal audit payload from GST registry — display via formatted helpers only */
  gst_registry_audit?: GSTRegistryAudit;
  created_at: string;
  updated_at: string;
}

/** Shown when POST /organizations returns 503 (GST re-verify upstream unavailable). */
export const ORG_CREATE_GST_VERIFICATION_UNAVAILABLE_MESSAGE =
  'Could not verify GST right now, try again.';

export class OrganizationRequestError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'OrganizationRequestError';
    this.status = status;
    Object.setPrototypeOf(this, OrganizationRequestError.prototype);
  }

  get isServiceUnavailable(): boolean {
    return this.status === 503;
  }

  static isInstance(error: unknown): error is OrganizationRequestError {
    return error instanceof OrganizationRequestError;
  }
}

function axiosMessage(error: AxiosError<{ message?: string; error?: { message?: string } }>): string {
  const data = error.response?.data;
  return (
    (typeof data?.message === 'string' && data.message) ||
    (typeof data?.error?.message === 'string' && data.error.message) ||
    error.message ||
    'Request failed'
  );
}

export type OrganizationMembershipRole = 'owner' | 'admin' | 'member' | 'viewer';
const normalizeMembershipRole = (role: string | null | undefined): OrganizationMembershipRole => {
  if (role === 'owner' || role === 'admin' || role === 'member' || role === 'viewer') return role;
  return 'viewer';
};

export interface UserOrganizationData {
  id: string;
  user_id: string;
  organization_id: string;
  role: OrganizationMembershipRole;
  status: 'active' | 'inactive';
  invited_by?: string;
  joined_at: string;
  organization: OrganizationData;
}

export interface UpdateOrganizationData {
  name?: string;
  email?: string;
  business_type?: string;
  industry_sector?: string;
  annual_turnover?: number;
  employee_count?: number;
  address?: {
    country: string;
    state: string;
    city: string;
    postal_code: string;
  };
}

export interface SwitchOrganizationResponse {
  success: boolean;
  message: string;
  data: {
    organization: OrganizationData;
    active_organization_id: string;
  };
}

export type OrganizationRole = OrganizationMembershipRole;

/** Roles the invite API accepts (org seat). RBAC job titles use GET /roles after the member joins. */
export const ORGANIZATION_INVITE_ROLES: readonly OrganizationRole[] = [
  'owner',
  'admin',
  'member',
  'viewer',
] as const;

export const ORGANIZATION_INVITE_ROLE_LABELS: Record<OrganizationRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

/** Display label for org seat. */
export const ORGANIZATION_MEMBERSHIP_ROLE_LABELS: Record<string, string> = {
  ...ORGANIZATION_INVITE_ROLE_LABELS,
};

export interface OrganizationInvitation {
  token: string;
  email: string;
  role: OrganizationRole;
  status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  message?: string;
  invited_by?: string;
  organization_id: string;
  organization_name?: string;
  created_at: string;
  expires_at?: string;
}

export interface AccessRequest {
  id: string;
  organization_id: string;
  organization_name?: string;
  requested_role: OrganizationRole;
  status: 'pending' | 'approved' | 'rejected';
  message?: string;
  review_message?: string;
  created_at: string;
  reviewed_at?: string;
  requester_email?: string;
}

const resolveOrgApiMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return fallback;
  const rawMessage =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message ||
    fallback;
  const normalized = String(rawMessage).toLowerCase();
  if (normalized.includes('already member')) return 'This user is already a member of the organization.';
  if (normalized.includes('pending invite') || normalized.includes('already invited')) {
    return 'An invitation is already pending for this user.';
  }
  if (normalized.includes('permission') || normalized.includes('forbidden')) {
    return 'You do not have permission to perform this action.';
  }
  if (normalized.includes('email mismatch')) {
    return 'This invitation was sent to a different email address.';
  }
  if (normalized.includes('expired')) return 'This invitation link has expired.';
  if (normalized.includes('cancelled') || normalized.includes('canceled')) {
    return 'This invitation was cancelled by the organization.';
  }
  if (normalized.includes('revoked')) return 'This invitation was revoked.';
  if (normalized.includes('invalid')) return 'This invitation link is invalid.';
  if (normalized.includes('already processed') || normalized.includes('already accepted') || normalized.includes('already rejected')) {
    return 'This invitation has already been processed.';
  }
  if (normalized.includes('pending access request') || normalized.includes('already requested')) {
    return 'You already have a pending access request for this organization.';
  }
  return String(rawMessage);
};

/** API list payloads may be T[] or nested objects; never pass a non-array to UI `.filter`. */
function coerceOrganizationList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>;
    for (const key of ['invitations', 'items', 'results', 'requests']) {
      const inner = o[key];
      if (Array.isArray(inner)) return inner as T[];
    }
    if (Array.isArray(o.data)) return o.data as T[];
    if (o.data !== undefined && typeof o.data === 'object' && o.data !== null) {
      return coerceOrganizationList<T>(o.data);
    }
  }
  return [];
}

export const organizationService = {
  mapOrganizationApiError(error: unknown, fallback: string): Error {
    return new Error(resolveOrgApiMessage(error, fallback));
  },
  /**
   * Switch to a different organization
   */
  async switchOrganization(organizationId: string): Promise<SwitchOrganizationResponse> {
    try {
      const response = await api.post(`/organizations/${organizationId}/switch`);
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || 'Failed to switch organization';
        throw new Error(message);
      }
      throw error;
    }
  },

  /**
   * Get user's organizations
   */
  async getUserOrganizations(): Promise<UserOrganizationData[]> {
    // Create cache key for this request
    const cacheKey = createApiCacheKey('/organizations/user');
    
    // Check cache first
    const cached = getCachedApiResponse<UserOrganizationData[]>(cacheKey);
    if (cached) {
      return cached;
    }
    
    try {
      const response = await api.get('/organizations/user');
      const data = (response.data.data || []).map((entry: any) => ({
        ...entry,
        role: normalizeMembershipRole(entry?.role),
      }));
      
      // Cache the response for 10 minutes
      cacheApiResponse(cacheKey, data, 10 * 60 * 1000);
      
      return data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || 'Failed to get organizations';
        throw new Error(message);
      }
      throw error;
    }
  },

  /**
   * Create a new organization
   */
  async createOrganization(organizationData: {
    name: string;
    gst_number: string;
    address: {
      country: string;
      state: string;
      city: string;
      postal_code: string;
    };
    email?: string;
    business_type?: string;
    industry_sector?: string;
    annual_turnover?: number;
    employee_count?: number;
  }): Promise<OrganizationData> {
    try {
      const response = await api.post('/organizations', organizationData);
      return response.data.data.organization;
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const status = _error.response?.status;
        const message = axiosMessage(_error) || 'Failed to create organization';
        throw new OrganizationRequestError(message, status);
      }
      throw _error;
    }
  },

  /**
   * Get organization details
   */
  async getOrganization(organizationId: string): Promise<OrganizationData> {
    try {
      const response = await api.get(`/organizations/${organizationId}`);
      return response.data.data;
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const message = _error.response?.data?.message || 'Failed to get organization';
        throw new Error(message);
      }
      throw _error;
    }
  },

  /**
   * Update organization
   */
  async updateOrganization(
    organizationId: string, 
    updateData: Partial<OrganizationData>
  ): Promise<OrganizationData> {
    try {
      const response = await api.put(`/organizations/${organizationId}`, updateData);
      return response.data.data;
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const message = _error.response?.data?.message || 'Failed to update organization';
        throw new Error(message);
      }
      throw _error;
    }
  },

  /**
   * Check GST number availability
   */
  async checkGstAvailability(gstNumber: string): Promise<{
    available: boolean;
    organization?: {
      id?: string;
      name: string;
      gst_number: string;
      primary_admin: {
        email: string;
        first_name: string;
        last_name: string;
      };
      primary_admin_email?: string;
    };
  }> {
    try {
      const response = await api.get(`/organizations/check-gst/${gstNumber}`);
      return response.data.data;
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const status = _error.response?.status;
        const message = axiosMessage(_error) || 'Failed to check GST availability';
        throw new OrganizationRequestError(message, status);
      }
      throw _error;
    }
  },

  /**
   * Check company name availability
   */
  async checkCompanyNameAvailability(companyName: string): Promise<{
    available: boolean;
    organization?: {
      name: string;
      gst_number: string;
      primary_admin: {
        email: string;
        first_name: string;
        last_name: string;
      };
    };
  }> {
    try {
      const encodedName = encodeURIComponent(companyName);
      const response = await api.get(`/organizations/check-name/${encodedName}`);
      return response.data.data;
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const message = _error.response?.data?.message || 'Failed to check company name availability';
        throw new Error(message);
      }
      throw _error;
    }
  },

  /**
   * Invite user to organization
   */
  async inviteUser(
    organizationId: string,
    email: string,
    role: OrganizationRole,
    message?: string
  ): Promise<void> {
    try {
      await api.post(`/organizations/${organizationId}/invite`, {
        email,
        role,
        message
      });
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to send invitation');
    }
  },

  async getOrganizationInvitations(organizationId: string): Promise<OrganizationInvitation[]> {
    try {
      const response = await api.get(`/organizations/${organizationId}/invitations`);
      return coerceOrganizationList<OrganizationInvitation>(response.data?.data ?? response.data);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to load invitations');
    }
  },

  async getMyInvitations(): Promise<OrganizationInvitation[]> {
    try {
      const response = await api.get('/organizations/my-invitations');
      return coerceOrganizationList<OrganizationInvitation>(response.data?.data ?? response.data);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to load your invitations');
    }
  },

  async acceptInvitation(token: string): Promise<void> {
    try {
      await api.post(`/organizations/invitations/${token}/accept`);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to accept invitation');
    }
  },

  async rejectInvitation(token: string): Promise<void> {
    try {
      await api.post(`/organizations/invitations/${token}/reject`);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to reject invitation');
    }
  },

  async cancelInvitation(token: string): Promise<void> {
    try {
      await api.post(`/organizations/invitations/${token}/cancel`);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to cancel invitation');
    }
  },

  async requestAccess(
    organizationId: string,
    requested_role: OrganizationRole,
    message?: string
  ): Promise<void> {
    try {
      await api.post(`/organizations/${organizationId}/request-access`, {
        requested_role,
        message,
      });
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to submit access request');
    }
  },

  async getMyAccessRequests(): Promise<AccessRequest[]> {
    try {
      const response = await api.get('/organizations/my-access-requests');
      return coerceOrganizationList<AccessRequest>(response.data?.data ?? response.data);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to load access requests');
    }
  },

  async getOrganizationAccessRequests(organizationId: string): Promise<AccessRequest[]> {
    try {
      const response = await api.get(`/organizations/${organizationId}/access-requests`);
      return coerceOrganizationList<AccessRequest>(response.data?.data ?? response.data);
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to load organization access requests');
    }
  },

  async reviewAccessRequest(
    requestId: string,
    decision: 'approve' | 'reject',
    review_message?: string
  ): Promise<void> {
    try {
      await api.post(`/organizations/access-requests/${requestId}/review`, {
        decision,
        review_message,
      });
    } catch (_error) {
      throw organizationService.mapOrganizationApiError(_error, 'Failed to review access request');
    }
  },

  /**
   * Get organization members
   */
  async getOrganizationMembers(organizationId: string): Promise<any[]> {
    try {
      const response = await api.get(`/organizations/${organizationId}/members`);
      return response.data.data;
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const message = _error.response?.data?.message || 'Failed to get organization members';
        throw new Error(message);
      }
      throw _error;
    }
  },

  /**
   * Remove user from organization
   */
  async removeUser(organizationId: string, userId: string): Promise<void> {
    try {
      await api.delete(`/organizations/${organizationId}/members/${userId}`);
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const message = _error.response?.data?.message || 'Failed to remove user';
        throw new Error(message);
      }
      throw _error;
    }
  },

  /**
   * Update user role in organization
   */
  async updateUserRole(
    organizationId: string,
    userId: string,
    role: OrganizationRole
  ): Promise<void> {
    try {
      await api.put(`/organizations/${organizationId}/members/${userId}`, { role });
    } catch (_error) {
      if (axios.isAxiosError(_error)) {
        const message = _error.response?.data?.message || 'Failed to update user role';
        throw new Error(message);
      }
      throw _error;
    }
  }
};